import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { PhaseTimer } from "../../components/game/PhaseTimer.js";
import { QrCode } from "../../components/game/QrCode.js";
import { SideBoard } from "../../components/game/SideBoard.js";
import { StallNotice } from "../../components/game/StallNotice.js";
import { Vinyl } from "../../components/game/Vinyl.js";
import type { GameRules, SideLabel, Snapshot } from "../../services/generated/client.js";
import { appUrl } from "../../services/serverOrigin.js";
import { cn } from "../../lib/utils.js";
import { sideLabel } from "../../lib/roster.js";
import {
  challengedSlotsFor,
  claimedSlotFor,
  guessTextFor,
  wonByChallenge,
} from "../../lib/placement.js";
import { NoticeToast } from "../../components/game/Notices.js";

/**
 * What the room is doing, in words big enough to read from the sofa.
 *
 * Words rather than an icon or a colour: this is read at three metres by
 * somebody holding a drink, and nothing on a display may be signalled by colour
 * alone (`.claude/rules/frontend.md`).
 */
function headline(board: Snapshot, t: TFunction): string {
  switch (board.phase) {
    case "turn_ready":
      return t("board.headline.ready");
    case "listening":
      return t("board.headline.listening");
    case "guessing":
      return t("board.headline.guessing");
    case "placing":
      return t("board.headline.placing");
    case "challenge":
      return t("board.headline.challenge");
    case "reveal":
    case "dispute":
      return t("board.headline.answer");
    case "settled":
      return t("board.headline.settled");
    case "finished":
      return t("board.headline.finished");
  }
}

/**
 * The ten-foot board.
 *
 * Every side side by side, the round in flight above them, and the answer when
 * it is allowed out. Built from `components/game/` rather than from a second set
 * of components — the same `Timeline` renders here and on the phone, which is
 * what keeps the two surfaces from drifting into different games.
 *
 * # Ten-foot rules are requirements, not suggestions
 *
 * No hover states — there is no pointer. Minimum type sizes. Nothing signalled
 * by colour alone. `env(safe-area-inset-*)` is respected globally in
 * `styles/index.css`, because TV overscan clips anything that assumes it owns
 * the edges. And no animation-heavy idle loop: this runs on a browser slower
 * than every phone in the room.
 */
export function Board({
  board,
  rules,
  roster,
  code,
  gameId = null,
  phaseEndsAt = null,
  phaseSeconds = null,
}: {
  board: Snapshot;
  rules: GameRules | null;
  /**
   * Who each side is.
   *
   * The television needed names as much as the phone did and never had them:
   * before phase 7 this wrote "Playing now" over the active side and a blank
   * over everybody else, which works only because the people it names are in
   * the room and can point.
   */
  roster: readonly SideLabel[];
  /** The room code, kept on screen so a latecomer can always join. */
  code: string;
  /**
   * Which game, for the recap link at the end.
   *
   * `null` before one starts, and for a television opened after one finished —
   * the id comes from the live row, so the screen that was there is the one that
   * can hand the link out.
   */
  gameId?: string | null;
  /**
   * When the phase in flight runs out, on this television's clock.
   *
   * `null` in a phase nothing is timing — and `null` on a television that has
   * just been switched on mid-round, until the next publication.
   */
  phaseEndsAt?: number | null;
  /** How long that phase gets in total. */
  phaseSeconds?: number | null;
}) {
  const { t } = useTranslation();
  const answer = board.round?.answer ?? null;
  // The app's own origin, not this page's: on a television this page lives
  // inside a package, and a QR of a `file://` path is not a link.
  const recapUrl = gameId ? appUrl(`/recap/${gameId}`) : null;
  const voided = board.voids_used > 0 && board.phase === "turn_ready";

  return (
    <div className="flex min-h-full flex-col gap-6 p-8">
      <header className="flex items-center justify-between gap-6">
        <div className="min-w-0">
          <p className="text-xl uppercase tracking-[0.3em] text-white/40">
            {t("round.number", { no: board.round_no })}
          </p>
          <h1 className="truncate text-6xl font-semibold">{headline(board, t)}</h1>
        </div>
        {/* The code, and only the code — a latecomer must be able to join out
            of the corner of the screen at any moment.

            📌 The countdown used to be here, beside the headline that it
            qualifies. It is on the record below now, for the reason the phone
            gave up its own header dial: the television was drawing **two
            discs**, a 132px clock here and a decorative record directly
            underneath. One disc, doing both jobs, is the same call — and one
            arrangement across every surface is the same reason `SideBoard` is
            one component and not two. */}
        <p className="shrink-0 text-4xl font-semibold tabular-nums tracking-[0.2em] text-flash-400">
          {code}
        </p>
      </header>

      {/* 🔴 **Why nothing is happening** — phase 21, ADR 0030. Above the record
          and before everything else, because a stalled party looks exactly like
          a working one that nobody is touching: the board is fine, the
          timelines are fine, and without this the room stares at a screen that
          is simply never going to move.

          Read-only here. The vote belongs on the phones — a television has no
          seat to attribute an answer to, and no way to press anything. */}
      {board.stall && board.phase !== "finished" && (
        <StallNotice stall={board.stall} big />
      )}

      {/* **Centred, the disc over the words** — the maintainer's call for the
          television. Side by side, the room's eye had two places to land and
          the countdown was competing with the answer rather than framing it.
          Stacked and centred, the disc is the thing you look at and the words
          under it say what it is counting. */}
      <section className="flex flex-col items-center gap-4 text-center">
        {/* 🔴 **The one disc**, and it is the clock — the same collapse the
            phone got. 200px rather than the 132 it was in the header: the dial
            is a fixed pixel size, so its *share* of the screen is what changes,
            and at 132 on 1920 the television's countdown was proportionally the
            **smallest** of the three surfaces while being the one read from
            three metres.

            Still a record when nothing is timing the phase — `TurnReady` waits
            for a person and a finished game waits for nobody, and a countdown
            towards nothing is the "why is it stuck?" a timer exists to answer.
            📌 Judged on a monitor, not on the actual television. */}
        {phaseEndsAt === null ? (
          <Vinyl spinning={board.phase === "listening"} size={200} />
        ) : (
          <PhaseTimer
            endsAt={phaseEndsAt}
            seconds={phaseSeconds}
            spinning={board.phase === "listening"}
            size={200}
          />
        )}

        <div className="w-full min-w-0">
          {answer ? (
            // The reveal. Present only from `Reveal` — the server does not send
            // it before, so there is nothing here to withhold.
            <div>
              {/* A null year inside a present answer is an undated round
                  (phase 19g), not a missing value. Rendered as words rather
                  than a number, and smaller, because it is not a year. */}
              {answer.year === null || answer.year === undefined ? (
                <p className="text-6xl font-semibold text-flash-400">
                  {t("round.undatedName")}
                </p>
              ) : (
                <p className="text-8xl font-semibold tabular-nums text-flash-400">
                  {answer.year}
                </p>
              )}
              {/* ⚠️ Not truncated. A centred line that ends in an ellipsis
                  reads as broken, and this is the one sentence the whole room
                  is waiting for — the same call as the cards, for the same
                  reason: a title you cannot finish reading is not an answer. */}
              <p className="mt-2 text-4xl font-semibold">{answer.title}</p>
              <p className="text-2xl text-white/60">{answer.artist}</p>
            </div>
          ) : board.round ? (
            <p className="text-3xl font-semibold text-white/50">
              {board.phase === "listening" ? t("board.listening") : t("board.answering")}
            </p>
          ) : (
            <p className="text-3xl font-semibold text-white/50">{t("board.waiting")}</p>
          )}
        </div>
      </section>

      {/* ⚠️ **Toast only, and large.** A dense scrolling log is unreadable at
          three metres and is exactly the animation-heavy idle loop the display
          rules warn about on an underpowered TV browser. The television says the
          one thing that just happened; the phone is where you catch up. */}
      <NoticeToast board={board} roster={roster} scale="room" />

      {voided && (
        // **A void is a deliberate do-over, and has to read as one** (ADR 0009).
        // A silent reset looks like the game glitched, and the whole correction
        // loop then reads as a bug rather than as a feature. Never the word
        // "error", never a spinner.
        <p
          role="status"
          className="rounded-2xl border-2 border-coin-400/50 bg-coin-400/10 px-6 py-4 text-3xl font-medium"
        >
          {t("board.voided")}
        </p>
      )}

      <section
        aria-label={t("board.timelines")}
        className={cn(
          "grid flex-1 gap-6",
          board.sides.length > 2 ? "grid-cols-2" : "grid-cols-1",
        )}
      >
        {board.sides.map((side) => (
          <SideBoard
            key={side.id}
            side={side}
            label={sideLabel(roster, side.id)}
            active={side.id === board.active_side}
            scale="room"
            // Phase 16a. The television is the surface the non-active players
            // are actually looking at during the challenge window, so a claim
            // nobody drew here is a mechanic nobody could aim.
            claimedSlot={claimedSlotFor(board, side.id)}
            // **Phase 18, finding #9.** The room's own screen was silent about
            // the contest: `challenged_slots` reached this component's props and
            // nothing here drew it, so in couch mode the argument happened on
            // four phones and not on the television everybody was watching.
            takenSlots={challengedSlotsFor(board, side.id, roster)}
            // **Finding #1.** And what they said it was — the claim the room
            // needs in order to have an opinion about the guess at all.
            guess={guessTextFor(board, side.id)}
            wonByChallenge={wonByChallenge(board, side.id)}
            // The television is where this matters most: it is the surface the
            // whole room is looking at when the answer lands, and the one that
            // had thirteen seconds of showing nothing between the reveal and the
            // card actually moving.
            outcome={
              board.round?.outcomes.find((outcome) => outcome.side === side.id) ?? null
            }
            {...(rules ? { cardsToWin: rules.cards_to_win } : {})}
          />
        ))}
      </section>

      {board.phase === "finished" && (
        // **Game over is the one moment everybody is looking at the television
        // and nobody is holding a controller.** So the board hands out the
        // recap the same way it hands out the join code: a QR to point a phone
        // at, and the URL in text for whoever is on the wrong network.
        <section
          aria-label={t("board.howItWent")}
          className="flex items-center justify-center gap-8 text-center"
        >
          <div>
            <p role="status" className="text-5xl font-semibold text-flash-400">
              {board.winner ? t("board.winner") : t("board.noWinner")}
            </p>
            {recapUrl && (
              <p className="mt-3 break-all text-2xl text-white/60">{recapUrl}</p>
            )}
          </div>
          {recapUrl && (
            <QrCode
              url={recapUrl}
              label={t("round.seeRecap")}
              className="w-56 shrink-0 rounded-2xl bg-stage-900 p-4 [&>svg]:w-full"
            />
          )}
        </section>
      )}
    </div>
  );
}
